import { emptyCalibration } from "./calibration";
import { emptyTankSizing } from "./tankSizing";

// v3 adds the conditional sensor-calibration step and the optional tank-size
// helper; loadDraft still accepts v2 drafts and preserves their form data.
export const DRAFT_VERSION = 3;

export const STEPS = {
  CUSTOMER_MODE: "customer_mode",
  CUSTOMER_SEARCH: "customer_search",
  CUSTOMER_NEW: "customer_new",
  ADDRESS_MODE: "address_mode",
  ADDRESS_SELECT: "address_select",
  ADDRESS_NEW: "address_new",
  ASSET_MODE: "asset_mode",
  ASSET_SELECT: "asset_select",
  ASSET_NEW: "asset_new",
  CONTACTS: "contacts",
  DEVICE: "device",
  CALIBRATION: "calibration",
  THRESHOLDS: "thresholds",
  NOTIFICATIONS: "notifications",
  PROVIDERS: "providers",
  REVIEW: "review",
  DONE: "done",
};

// The six primary sections shown to the installer; sub-steps map onto them.
export const SECTIONS = [
  { key: "customer", label: "Customer" },
  { key: "address", label: "Address" },
  { key: "contacts", label: "Contacts & device access" },
  { key: "asset", label: "Tank / asset & device" },
  { key: "providers", label: "Provider sharing" },
  { key: "review", label: "Review & confirm" },
];

export const STEP_SECTION = {
  [STEPS.CUSTOMER_MODE]: "customer",
  [STEPS.CUSTOMER_SEARCH]: "customer",
  [STEPS.CUSTOMER_NEW]: "customer",
  [STEPS.ADDRESS_MODE]: "address",
  [STEPS.ADDRESS_SELECT]: "address",
  [STEPS.ADDRESS_NEW]: "address",
  [STEPS.CONTACTS]: "contacts",
  [STEPS.ASSET_MODE]: "asset",
  [STEPS.ASSET_SELECT]: "asset",
  [STEPS.ASSET_NEW]: "asset",
  [STEPS.DEVICE]: "asset",
  [STEPS.CALIBRATION]: "asset",
  [STEPS.THRESHOLDS]: "asset",
  [STEPS.NOTIFICATIONS]: "asset",
  [STEPS.PROVIDERS]: "providers",
  [STEPS.REVIEW]: "review",
};

const TAIL_STEPS = [STEPS.PROVIDERS, STEPS.REVIEW];

// Calibration sits between the monitor and thresholds only for level devices
// (device type 4), as reported by the installation overview. Eligibility is
// never inferred from a model name, tank name or external device ID.
function deviceSteps(state) {
  return [
    STEPS.DEVICE,
    ...(Number(state.deviceTypeId) === 4 ? [STEPS.CALIBRATION] : []),
    STEPS.THRESHOLDS,
    STEPS.NOTIFICATIONS,
  ];
}

export function deriveSteps(state) {
  const { branch } = state;
  if (!branch.customerMode) return [STEPS.CUSTOMER_MODE];
  // A new customer has no saved sites or assets, so both are always new.
  if (branch.customerMode === "new") {
    return [
      STEPS.CUSTOMER_MODE, STEPS.CUSTOMER_NEW, STEPS.ADDRESS_NEW, STEPS.CONTACTS,
      STEPS.ASSET_NEW, ...deviceSteps(state), ...TAIL_STEPS,
    ];
  }
  const steps = [STEPS.CUSTOMER_MODE, STEPS.CUSTOMER_SEARCH];
  if (!branch.addressMode) return [...steps, STEPS.ADDRESS_MODE];
  steps.push(STEPS.ADDRESS_MODE);
  if (branch.addressMode === "new") {
    steps.push(STEPS.ADDRESS_NEW, STEPS.CONTACTS, STEPS.ASSET_NEW);
  } else {
    steps.push(STEPS.ADDRESS_SELECT, STEPS.CONTACTS);
    if (!branch.assetMode) return [...steps, STEPS.ASSET_MODE];
    if (branch.assetMode === "existing") steps.push(STEPS.ASSET_MODE, STEPS.ASSET_SELECT);
    else steps.push(STEPS.ASSET_MODE, STEPS.ASSET_NEW);
  }
  return [...steps, ...deviceSteps(state), ...TAIL_STEPS];
}

export function initialState(claimCode) {
  return {
    version: DRAFT_VERSION,
    claimCode,
    stepIndex: 0,
    branch: { customerMode: undefined, addressMode: undefined, assetMode: undefined },
    customer: null,
    customerForm: null,
    address: null,
    addressForm: null,
    asset: null,
    assetForm: null,
    contacts: [],
    contactAccess: {},
    billingContactKey: null,
    device: { device_name: "", map_identifier: "", installation_date: today() },
    deviceTypeId: null,
    calibration: emptyCalibration(),
    tankSizing: emptyTankSizing(),
    tankGeometry: null,
    // Optional device-test evidence for review only; never holds tokens.
    deviceTest: null,
    thresholds: [],
    thresholdLookups: null,
    alerts: { enabled: false, recipientKeys: [] },
    providerOptions: [],
    providerSelection: [],
    attest: { accuracy: false, terms: false },
    progress: {
      customer: null,
      address: null,
      asset: null,
      installation: null,
      contacts: {},
      plan: null,
      claimId: null,
    },
    result: null,
    updatedAt: null,
  };
}

export function today() {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function stamp(state, patch) {
  return { ...state, ...patch, updatedAt: new Date().toISOString() };
}

function staleDeviceTest(record) {
  return record ? { ...record, stale: true, measurement_confirmed: false } : null;
}

function clearCustomerDownstream(state) {
  return {
    ...state,
    branch: { customerMode: state.branch.customerMode, addressMode: undefined, assetMode: undefined },
    customer: null,
    address: null,
    addressForm: null,
    asset: null,
    assetForm: null,
    contacts: [],
    contactAccess: {},
    billingContactKey: null,
    alerts: { enabled: false, recipientKeys: [] },
    providerSelection: [],
    // Target-change resets: measurements and accepted estimates belong to the
    // previous customer/address/tank and must be re-entered and re-reviewed.
    calibration: emptyCalibration(),
    tankSizing: emptyTankSizing(),
    tankGeometry: null,
    deviceTest: null,
    attest: { accuracy: false, terms: false },
  };
}

function clearAddressDownstream(state) {
  return {
    ...state,
    branch: { ...state.branch, assetMode: undefined },
    asset: null,
    assetForm: null,
    calibration: emptyCalibration(),
    tankSizing: emptyTankSizing(),
    tankGeometry: null,
    deviceTest: null,
    attest: { ...state.attest, accuracy: false },
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case "HYDRATE":
      return stamp(state, action.payload);
    case "RESTORE_DRAFT":
      return stamp({ ...state, ...action.payload, result: null }, {});
    case "GO_TO":
      return { ...state, stepIndex: action.index };
    case "NEXT": {
      const steps = deriveSteps(state);
      return { ...state, stepIndex: Math.min(state.stepIndex + 1, steps.length - 1) };
    }
    case "BACK":
      return { ...state, stepIndex: Math.max(state.stepIndex - 1, 0) };
    case "SET_CUSTOMER_MODE": {
      const next = clearCustomerDownstream(state);
      const isNew = action.mode === "new";
      return stamp(next, {
        branch: {
          ...next.branch,
          customerMode: action.mode,
          addressMode: isNew ? "new" : undefined,
          assetMode: isNew ? "new" : undefined,
        },
        customerForm: isNew ? state.customerForm || emptyCustomerForm() : null,
        addressForm: isNew ? state.addressForm || emptyAddressForm() : null,
        assetForm: isNew ? state.assetForm || emptyAssetForm() : null,
      });
    }
    case "SELECT_CUSTOMER": {
      const next = clearCustomerDownstream(state);
      return stamp(next, { customer: action.customer });
    }
    case "SET_CUSTOMER_FORM":
      return stamp(state, { customerForm: { ...state.customerForm, ...action.form } });
    case "SET_ADDRESS_MODE": {
      const next = clearAddressDownstream(state);
      return stamp(next, {
        branch: { ...next.branch, addressMode: action.mode },
        addressForm: action.mode === "new" ? emptyAddressForm() : null,
      });
    }
    case "SELECT_ADDRESS": {
      const next = clearAddressDownstream(state);
      return stamp(next, { address: action.address });
    }
    case "SET_ADDRESS_FORM":
      return stamp(state, { addressForm: { ...state.addressForm, ...action.form } });
    case "SET_ASSET_MODE": {
      return stamp({
        ...state,
        asset: null,
        assetForm: null,
        calibration: emptyCalibration(),
        tankSizing: emptyTankSizing(),
        tankGeometry: null,
        deviceTest: null,
        attest: { ...state.attest, accuracy: false },
      }, {
        branch: { ...state.branch, assetMode: action.mode },
        assetForm: action.mode === "new" ? emptyAssetForm() : null,
      });
    }
    case "SELECT_ASSET":
      return stamp(state, {
        asset: action.asset,
        calibration: emptyCalibration(),
        tankSizing: emptyTankSizing(),
        tankGeometry: null,
        deviceTest: null,
        attest: { ...state.attest, accuracy: false },
      });
    // Editing the original tank form invalidates an accepted helper override
    // so a stale calculated size can never be submitted silently.
    case "SET_ASSET_FORM":
      return stamp(state, {
        assetForm: { ...state.assetForm, ...action.form },
        tankSizing: emptyTankSizing(),
        tankGeometry: null,
        deviceTest: staleDeviceTest(state.deviceTest),
        attest: { ...state.attest, accuracy: false },
      });
    case "SET_CALIBRATION":
      return stamp(state, {
        calibration: { ...state.calibration, ...action.patch },
        deviceTest: staleDeviceTest(state.deviceTest),
        attest: { ...state.attest, accuracy: false },
      });
    case "SET_TANK_SIZING":
      return stamp(state, {
        tankSizing: action.value,
        tankGeometry: null,
        deviceTest: staleDeviceTest(state.deviceTest),
        attest: { ...state.attest, accuracy: false },
      });
    case "ACCEPT_TANK_GEOMETRY":
      return stamp(state, {
        tankGeometry: action.geometry,
        deviceTest: staleDeviceTest(state.deviceTest),
        attest: { ...state.attest, accuracy: false },
      });
    case "CLEAR_TANK_GEOMETRY":
      return stamp(state, {
        tankGeometry: null,
        deviceTest: staleDeviceTest(state.deviceTest),
        attest: { ...state.attest, accuracy: false },
      });
    // Device-test evidence. A retained result stays visible for review, but
    // a confirmation never survives a settings change or a new test.
    case "SET_DEVICE_TEST": {
      const previous = state.deviceTest;
      const same = previous?.test_id && previous.test_id === action.record.test_id;
      return stamp(state, {
        deviceTest: {
          ...action.record,
          stale: same ? Boolean(previous.stale) : false,
          measurement_confirmed: same && action.record.status === "received" && !previous.stale
            ? Boolean(previous.measurement_confirmed)
            : false,
        },
      });
    }
    case "SET_DEVICE_TEST_STATUS":
      if (!state.deviceTest || state.deviceTest.status !== "waiting") return state;
      return stamp(state, { deviceTest: { ...state.deviceTest, status: action.status, measurement_confirmed: false } });
    case "CONFIRM_DEVICE_TEST":
      if (state.deviceTest?.status !== "received" || state.deviceTest.stale ||
          state.deviceTest.measurement_status !== "valid") return state;
      return stamp(state, { deviceTest: { ...state.deviceTest, measurement_confirmed: Boolean(action.confirmed) } });
    case "SET_CONTACTS":
      return stamp(state, { contacts: action.contacts, attest: { ...state.attest, accuracy: false } });
    case "SET_CONTACT_ACCESS":
      return stamp(state, {
        contactAccess: {
          ...state.contactAccess,
          [action.key]: { access_type: "read", service_access_type: "read", ...state.contactAccess?.[action.key], ...action.patch },
        },
        attest: { ...state.attest, accuracy: false },
      });
    case "SET_ALERTS":
      return stamp(state, { alerts: action.alerts, attest: { ...state.attest, accuracy: false } });
    case "SET_BILLING_CONTACT": {
      const billingContactKey =
        state.billingContactKey === action.key ? null : action.key;
      return stamp(state, { billingContactKey });
    }
    case "SET_DEVICE":
      return stamp(state, {
        device: { ...state.device, ...action.device },
        attest: { ...state.attest, accuracy: false },
      });
    case "SET_THRESHOLDS":
      return stamp(state, { thresholds: action.thresholds, attest: { ...state.attest, accuracy: false } });
    case "SET_THRESHOLD_LOOKUPS":
      return stamp(state, { thresholdLookups: action.lookups });
    case "SET_PROVIDER_OPTIONS":
      return stamp(state, { providerOptions: action.options });
    case "TOGGLE_PROVIDER": {
      const has = state.providerSelection.includes(action.providerId);
      return stamp(state, {
        providerSelection: has
          ? state.providerSelection.filter((id) => id !== action.providerId)
          : [...state.providerSelection, action.providerId],
      });
    }
    case "SET_ATTEST":
      return { ...state, attest: { ...state.attest, ...action.attest } };
    case "MARK_PROGRESS":
      return stamp(state, { progress: { ...state.progress, ...action.progress } });
    case "SET_RESULT":
      return stamp(state, { result: action.result });
    case "RESET":
      return initialState(state.claimCode);
    default:
      return state;
  }
}

export function emptyCustomerForm() {
  return {
    entity_type_id: "",
    company_id: "",
    status_type_id: "",
    name: "",
    first_name: "",
    last_name: "",
  };
}

export function emptyAddressForm() {
  return {
    address_type_id: "",
    name: "",
    street_address1: "",
    street_address2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    latitude: "",
    longitude: "",
    status_type_id: "",
  };
}

export function emptyAssetForm() {
  return {
    name: "",
    item_id: "",
    status_type_id: "",
    map_identifier: "",
    shape: "rectangle",
    capacity_mode: "measurements",
    dims: { length_in: "", width_in: "", depth_in: "", diameter_in: "" },
    capacity_gallons: "",
    max_fill_pct: "",
    ratio: "",
    attributes: {},
  };
}

export function contactKey(contact, index) {
  if (contact.id) return `c${contact.id}`;
  if (contact.client_ref) return contact.client_ref;
  return `n${index}`;
}

export function newClientRef(prefix = "contact") {
  const random = typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}-${random}`.slice(0, 64);
}

export function draftKey(claimCode) {
  return `rivio_install_draft:${claimCode}`;
}

export function loadDraft(claimCode) {
  try {
    const raw = localStorage.getItem(draftKey(claimCode));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // v2 drafts predate the conditional calibration step; they still restore.
    if (![2, DRAFT_VERSION].includes(parsed.version)) return null;
    if (parsed.claimCode !== claimCode) return null;
    const { result, attest, ...safe } = parsed;
    const storedSizing = safe.tankSizing || {};
    const freshSizing = emptyTankSizing();
    // Resuming starts at the first step so a stored numeric index can never
    // open the wrong page after a conditional step was added. Form data is
    // preserved; device type must be reloaded from the server.
    return {
      ...safe,
      version: DRAFT_VERSION,
      deviceTypeId: null,
      stepIndex: 0,
      calibration: { ...emptyCalibration(), ...safe.calibration, scale_min_eng: "0" },
      tankSizing: { ...freshSizing, ...storedSizing, dims: { ...freshSizing.dims, ...storedSizing.dims } },
      tankGeometry: safe.tankGeometry || null,
      // Tests are not resumed across reloads; review shows "Not performed".
      deviceTest: null,
      attest: { accuracy: false, terms: false },
      result: null,
    };
  } catch {
    return null;
  }
}

export function saveDraft(state) {
  try {
    const { result, ...rest } = state;
    localStorage.setItem(draftKey(state.claimCode), JSON.stringify({ ...rest, result: null }));
    return true;
  } catch {
    return false;
  }
}

export function clearDraft(claimCode) {
  try {
    localStorage.removeItem(draftKey(claimCode));
  } catch {
    /* storage unavailable */
  }
}
